import { useState, useMemo } from 'react';
import { Plus, Search } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Checkbox } from '@/components/ui/checkbox';
import { Label } from '@/components/ui/label';
import { ErrorAlert } from '@/components/common/ErrorAlert';
import { SubcontractorList } from '../components/SubcontractorList';
import { SubcontractorForm } from '../components/SubcontractorForm';
import { useSubcontractors } from '../hooks/useSubcontractors';
import { useAuth } from '@/hooks/useAuth';
import { Permissions } from '@/types';
import { deleteSubcontractor } from '../api/subcontractors';
import type { SubcontractorDto } from '../types';

export function SubcontractorsPage() {
  const [showInactive, setShowInactive] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [selectedSubcontractor, setSelectedSubcontractor] = useState<SubcontractorDto | null>(null);

  const { hasPermission } = useAuth();
  const canCreateSubcontractors = hasPermission(Permissions.SubcontractorsCreate);
  const canUpdateSubcontractors = hasPermission(Permissions.SubcontractorsUpdate);

  // Fetch all subcontractors or only active based on toggle
  const { data: subcontractors, isLoading, error, refetch } = useSubcontractors(
    showInactive ? undefined : { isActive: true }
  );

  // Filter subcontractors by search query
  const filteredSubcontractors = useMemo(() => {
    if (!searchQuery.trim()) {
      return subcontractors;
    }
    const query = searchQuery.toLowerCase();
    return subcontractors.filter(
      (sub) =>
        sub.companyName.toLowerCase().includes(query) ||
        sub.contactName?.toLowerCase().includes(query) ||
        sub.email?.toLowerCase().includes(query) ||
        sub.officePhone?.includes(query) ||
        sub.cellPhone?.includes(query)
    );
  }, [subcontractors, searchQuery]);

  const handleAddSubcontractor = () => {
    setSelectedSubcontractor(null);
    setIsFormOpen(true);
  };

  const handleEditSubcontractor = (subcontractor: SubcontractorDto) => {
    setSelectedSubcontractor(subcontractor);
    setIsFormOpen(true);
  };

  const handleDeactivateSubcontractor = async (subcontractor: SubcontractorDto) => {
    if (!window.confirm(`Are you sure you want to deactivate "${subcontractor.companyName}"?`)) {
      return;
    }

    try {
      await deleteSubcontractor(subcontractor.id);
      refetch();
    } catch (err) {
      const error = err as { response?: { data?: { message?: string } } };
      alert(error.response?.data?.message || 'Failed to deactivate subcontractor');
    }
  };

  const handleFormSuccess = () => {
    refetch();
  };

  return (
    <div className="min-h-screen bg-white px-5 py-6 space-y-4">
      {/* Header */}
      <div className="bg-white rounded-[10px] p-5 space-y-4">
        <div className="flex items-center justify-between flex-wrap gap-4">
          {/* Left side: Title */}
          <h2 className="text-lg font-semibold text-[#0a0a9e]">Subcontractors</h2>

          {/* Right side: Search, Show Inactive, Add Button */}
          <div className="flex items-center gap-4">
            {/* Search Input */}
            <div className="relative">
              <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              <Input
                placeholder="Search subcontractors..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9 w-[250px]"
              />
            </div>

            {/* Show Inactive Toggle */}
            <div className="flex items-center space-x-2">
              <Checkbox
                id="showInactive"
                checked={showInactive}
                onChange={(e) => setShowInactive(e.target.checked)}
              />
              <Label htmlFor="showInactive" className="text-sm font-normal cursor-pointer">
                Show inactive
              </Label>
            </div>

            {/* Add Button - requires create permission */}
            {canCreateSubcontractors && (
              <Button onClick={handleAddSubcontractor} className="gap-2">
                <Plus className="h-4 w-4" />
                Add Subcontractor
              </Button>
            )}
          </div>
        </div>
      </div>

      <ErrorAlert error={error} className="rounded-[20px] p-4" />

      {/* Subcontractor List */}
      <div className="bg-white rounded-[20px] p-4 border border-[#d9d9d9]">
        <SubcontractorList
          subcontractors={filteredSubcontractors}
          onEdit={handleEditSubcontractor}
          onDeactivate={handleDeactivateSubcontractor}
          isLoading={isLoading}
          canEdit={canUpdateSubcontractors}
        />
      </div>

      {/* Add/Edit Subcontractor Form Dialog */}
      <SubcontractorForm
        open={isFormOpen}
        onOpenChange={setIsFormOpen}
        subcontractor={selectedSubcontractor}
        onSuccess={handleFormSuccess}
      />
    </div>
  );
}
